import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { $createLinkNode, $isLinkNode } from "@lexical/link";
import {
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  LexicalEditor,
  $createTextNode,
  $insertNodes,
  COMMAND_PRIORITY_EDITOR,
  RangeSelection,
} from "lexical";
import { mergeRegister } from "@lexical/utils";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { Link } from "lucide-react";
import { INSERT_TEMPLATE_COMMAND, TextTemplate } from "./AddNamePopover";
import { getSelectedNode } from "../utils/selectionUtils";

export function LinkPopover({
  isLink,
  editor,
}: {
  isLink: boolean;
  editor: LexicalEditor;
}) {
  const { t } = useTranslation();

  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState<string>("");
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null,
  );
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        INSERT_TEMPLATE_COMMAND,
        (type: TextTemplate) => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) {
            return false;
          }

          const focusNode = selection.focus.getNode();

          if (focusNode !== null && type) {
            const textNode = $createTextNode(
              type === "student"
                ? "{student_first_name}"
                : "{sender_first_name}",
            );
            selection.insertNodes([textNode]);
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  const getValues = () => {
    editor.read(() => {
      const selection = $getSelection();

      setLastSelection(selection);
      setLinkText(selection?.getTextContent() ?? "");
    });
  };

  const handleLinkSubmission = (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setOpen(false);

    if (linkUrl !== "") {
      if (lastSelection !== null) {
        editor.update(() => {
          const selection = lastSelection;
          if ($isRangeSelection(selection)) {
            const linkContent = linkText || linkUrl;
            editor?.update(
              () => {
                const linkNode = getLinkNodeInSelection(selection);
                const node = $createLinkNode(linkUrl);
                node.append($createTextNode(linkContent));
                if (linkNode) {
                  linkNode.replace(node);
                  node.select();
                } else {
                  $insertNodes([node]);
                  node.select();
                }
              },
              { discrete: true },
            );
          }
        });
      }
    }
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          getValues();
        } else {
          setLinkUrl("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant={isLink ? "secondary" : "ghost"}
          size="sm"
          type="button"
          className="h-8 w-8 p-0"
          aria-label={t("bulkMessaging.composeMessage.addLink")}
          onClick={() => setOpen(!isOpen)}
        >
          <Link className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-87 px-6 py-0" align="start">
        <div className="space-y-6">
          <h4 className="pt-[17px] text-sm font-bold">
            {t("bulkMessaging.composeMessage.addLink")}
          </h4>
          <Input
            placeholder={t("bulkMessaging.composeMessage.linkTextPlaceholder")}
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            className="w-full"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
              }
            }}
          />
          <Input
            placeholder={t("bulkMessaging.composeMessage.linkPlaceholder")}
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleLinkSubmission(e);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            className="w-full"
          />
          <div className="flex justify-end pb-[18px]">
            <Button
              size="sm"
              type="button"
              onClick={handleLinkSubmission}
              disabled={!linkUrl.trim()}
            >
              {t("bulkMessaging.composeMessage.apply")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function getLinkNodeInSelection(selection: RangeSelection | null) {
  if (!selection) {
    return null;
  }
  const node = getSelectedNode(selection);
  if (node === null) {
    return null;
  }
  const parent = node.getParent();
  if ($isLinkNode(parent)) {
    return parent;
  } else if ($isLinkNode(node)) {
    return node;
  }
  return null;
}
